import React, { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, Lock, User, Eye, EyeOff, AlertTriangle, CheckCircle2, ArrowRight, Loader2 } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";

export default function Login() {
  const { t, lang } = useLang();
  const { loginWithPassword, registerWithPassword } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const redirect = params.get("redirect") || "/dashboard";

  const [mode, setMode] = useState("login"); // "login" | "register"
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const startGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + redirect;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMsg("");

    if (!email.trim() || !password.trim()) {
      setError(lang === "fr" ? "Veuillez remplir tous les champs obligatoires." : "Please fill in all required fields.");
      return;
    }

    if (mode === "register" && !name.trim()) {
      setError(lang === "fr" ? "Veuillez renseigner votre nom complet." : "Please enter your full name.");
      return;
    }

    if (password.length < 6) {
      setError(lang === "fr" ? "Le mot de passe doit contenir au moins 6 caractères." : "Password must be at least 6 characters.");
      return;
    }

    try {
      setLoading(true);
      if (mode === "register") {
        await registerWithPassword(name, email, password);
        setSuccessMsg(lang === "fr" ? "Compte créé avec succès ! Redirection..." : "Account created successfully! Redirecting...");
      } else {
        await loginWithPassword(email, password);
        setSuccessMsg(lang === "fr" ? "Connexion réussie ! Redirection..." : "Login successful! Redirecting...");
      }

      setTimeout(() => {
        navigate(redirect);
      }, 500);
    } catch (err) {
      const msg =
        err.response?.data?.detail ||
        (lang === "fr"
          ? "Une erreur est survenue lors de l'authentification."
          : "An error occurred during authentication.");
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-alabaster min-h-[85vh] flex items-center justify-center py-12 px-4 sm:px-6" data-testid="login-page">
      <div className="max-w-md w-full">
        {/* Header Header */}
        <div className="text-center mb-8">
          <p className="text-[10px] tracking-[0.3em] uppercase text-terracotta mb-2">— {t("nav.login")}</p>
          <h1 className="font-serif text-3xl sm:text-4xl text-navy tracking-tight leading-tight">
            {mode === "login"
              ? lang === "fr"
                ? "Espace Client JNIC"
                : "JNIC Client Portal"
              : lang === "fr"
              ? "Créer mon Compte"
              : "Create Client Account"}
          </h1>
          <p className="text-navy/60 text-xs sm:text-sm mt-2 max-w-sm mx-auto">
            {lang === "fr"
              ? "Accédez à vos documents d'immigration, vos réservations et vos cours TCF."
              : "Access your immigration documents, bookings, and TCF courses."}
          </p>
        </div>

        {/* Main Card */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl p-6 sm:p-8 border border-navy/10 shadow-sm"
        >
          {/* Mode Switcher Tabs */}
          <div className="grid grid-cols-2 gap-1 p-1 bg-navy/5 rounded-2xl mb-6 border border-navy/5">
            <button
              type="button"
              onClick={() => {
                setMode("login");
                setError("");
              }}
              className={`py-2 text-xs font-semibold rounded-xl transition ${
                mode === "login" ? "bg-white text-navy shadow-xs" : "text-navy/60 hover:text-navy"
              }`}
            >
              {lang === "fr" ? "Se connecter" : "Log In"}
            </button>
            <button
              type="button"
              onClick={() => {
                setMode("register");
                setError("");
              }}
              className={`py-2 text-xs font-semibold rounded-xl transition ${
                mode === "register" ? "bg-white text-navy shadow-xs" : "text-navy/60 hover:text-navy"
              }`}
            >
              {lang === "fr" ? "Créer un compte" : "Sign Up"}
            </button>
          </div>

          {/* Google 1-Click Login Button */}
          <button
            type="button"
            onClick={startGoogleLogin}
            data-testid="google-login-button"
            className="w-full flex items-center justify-center gap-3 bg-white text-navy border border-navy/20 hover:border-navy/40 px-4 py-3 rounded-2xl shadow-2xs hover:bg-alabaster transition group"
          >
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
              <path
                d="M12 10.8v2.9h4.8c-.2 1.3-1.5 3.9-4.8 3.9-2.9 0-5.2-2.4-5.2-5.3s2.3-5.3 5.2-5.3c1.7 0 2.8.7 3.4 1.3l2.3-2.2C16.1 4.6 14.3 4 12 4 7.6 4 4 7.6 4 12s3.6 8 8 8c4.6 0 7.6-3.2 7.6-7.8 0-.5-.1-.9-.1-1.4H12z"
                fill="#0B192C"
              />
            </svg>
            <span className="text-xs font-semibold tracking-wide">
              {lang === "fr" ? "Continuer avec Google" : "Continue with Google"}
            </span>
          </button>

          {/* Divider */}
          <div className="relative flex items-center justify-center my-6">
            <div className="border-t border-navy/10 w-full" />
            <span className="bg-white px-3 text-[11px] uppercase tracking-wider text-navy/40 font-medium">
              {lang === "fr" ? "ou par email" : "or with email"}
            </span>
            <div className="border-t border-navy/10 w-full" />
          </div>

          {/* Email / Password Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === "register" && (
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wider text-navy mb-1.5">
                  {lang === "fr" ? "Nom & Prénom" : "Full Name"}
                </label>
                <div className="relative">
                  <User size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-navy/40" />
                  <input
                    type="text"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={lang === "fr" ? "Ex: Jean-Marc Dupont" : "E.g. John Doe"}
                    className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-navy/20 text-xs sm:text-sm focus:outline-hidden focus:border-terracotta bg-white"
                  />
                </div>
              </div>
            )}

            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-navy mb-1.5">
                {lang === "fr" ? "Adresse e-mail" : "Email Address"}
              </label>
              <div className="relative">
                <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-navy/40" />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="nom@exemple.com"
                  className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-navy/20 text-xs sm:text-sm focus:outline-hidden focus:border-terracotta bg-white"
                />
              </div>
            </div>

            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-navy mb-1.5">
                {lang === "fr" ? "Mot de passe" : "Password"}
              </label>
              <div className="relative">
                <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-navy/40" />
                <input
                  type={showPassword ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full pl-10 pr-10 py-2.5 rounded-xl border border-navy/20 text-xs sm:text-sm focus:outline-hidden focus:border-terracotta bg-white"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-navy/40 hover:text-navy p-1"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {mode === "register" && (
                <p className="text-[10px] text-navy/40 mt-1">
                  {lang === "fr" ? "Au moins 6 caractères." : "At least 6 characters."}
                </p>
              )}
            </div>

            {error && (
              <motion.div
                initial={{ opacity: 0, y: -5 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-xs flex items-start gap-2"
              >
                <AlertTriangle size={15} className="shrink-0 mt-0.5" />
                <span>{error}</span>
              </motion.div>
            )}

            {successMsg && (
              <motion.div
                initial={{ opacity: 0, y: -5 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs flex items-center gap-2"
              >
                <CheckCircle2 size={15} className="shrink-0" />
                <span>{successMsg}</span>
              </motion.div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full mt-2 py-3 px-4 rounded-xl text-xs sm:text-sm font-semibold bg-terracotta hover:bg-[#B91219] text-white transition disabled:opacity-50 flex items-center justify-center gap-2 shadow-sm"
            >
              {loading && <Loader2 size={16} className="animate-spin" />}
              <span>
                {loading
                  ? lang === "fr"
                    ? "Patientez..."
                    : "Please wait..."
                  : mode === "login"
                  ? lang === "fr"
                    ? "Se connecter"
                    : "Log In"
                  : lang === "fr"
                  ? "Créer mon compte"
                  : "Create Account"}
              </span>
              {!loading && <ArrowRight size={14} />}
            </button>
          </form>
        </motion.div>

        {/* Back Link */}
        <p className="text-center text-xs text-navy/50 mt-6">
          <Link to="/" className="hover:text-navy underline">
            {lang === "fr" ? "← Retourner à l'accueil" : "← Back to Homepage"}
          </Link>
        </p>
      </div>
    </div>
  );
}

